import { collection, query, where, limit, getDocs, getDoc, doc } from 'firebase/firestore'

// Backend writes suppliers[associateId].item_codes to companies/{cid}/items synchronously;
// warehouse extended_items are overwritten asynchronously, so the lookup starts from company items.
export function buildSupplierCodeQuery (db, companyId, associateId, code) {
  return query(
    collection(db, `companies/${companyId}/items`),
    where(`suppliers.${associateId}.item_codes`, 'array-contains', code),
    where('status', '==', 'OK'),
    limit(2)
  )
}

export async function findItemIdsBySupplierCode (db, companyId, associateId, code) {
  const snapshot = await getDocs(buildSupplierCodeQuery(db, companyId, associateId, code))
  return snapshot.docs.map(item => item.id)
}

export async function loadExtendedItems (db, warehouseId, itemIds) {
  const snapshots = await Promise.all(itemIds.map(id => getDoc(doc(db, `warehouses/${warehouseId}/extended_items/${id}`))))
  return snapshots.filter(snapshot => snapshot.exists()).map(snapshot => snapshot.data())
}

export function resolveAutoMatch (matches) {
  if (matches.length === 0) return { status: 'none' }
  if (matches.length === 1) return { status: 'matched', item: matches[0] }
  return { status: 'ambiguous', items: matches }
}

export async function autoMatchBySupplierCode (db, { companyId, warehouseId }, associateId, code) {
  const ids = await findItemIdsBySupplierCode(db, companyId, associateId, code)
  if (ids.length !== 1) return resolveAutoMatch(ids)
  return resolveAutoMatch(await loadExtendedItems(db, warehouseId, ids))
}

export function withLearnedCode (suppliers, associate, code) {
  const trimmed = String(code || '').trim()
  if (!trimmed) throw new Error('Supplier item code is empty')
  if (!associate || !associate.name || !String(associate.name).trim()) {
    throw new Error('Associate name is required')
  }
  const map = suppliers || {}
  const entries = Object.keys(map).filter(key => map[key]).map(key => ({ ...map[key], id: map[key].id || key }))
  const index = entries.findIndex(entry => entry.id === associate.id)
  if (index === -1) {
    entries.push({ id: associate.id, name: associate.name, item_name: '', item_code: trimmed, item_codes: [trimmed] })
    return entries
  }
  const current = entries[index]
  const codes = current.item_codes || []
  entries[index] = {
    ...current,
    item_code: current.item_code || trimmed,
    item_codes: codes.includes(trimmed) ? codes : [...codes, trimmed]
  }
  return entries
}
